/**
 * Flights page main script
 * Handles flight search functionality, results display, and filtering
 */

import { HeaderComponent } from './components/HeaderComponent';
import { FlightSearchComponent } from './components/FlightSearchComponent';
import { FlightResultsComponent } from './components/FlightResultsComponent';
import { FlightFiltersComponent } from './components/FlightFiltersComponent';
import { FlightSearchData, FlightResult, FlightFilters } from './types/data-models';

class FlightsPage {
  private searchComponent: FlightSearchComponent | null = null;
  private resultsComponent: FlightResultsComponent | null = null;
  private filtersComponent: FlightFiltersComponent | null = null;
  private currentSearchData: FlightSearchData | null = null;
  private currentFilters: FlightFilters = {};

  constructor() {
    this.init();
  }

  private init(): void {
    this.initializeComponents();
    this.bindEvents();
    this.handleInitialSearch();
  }

  private initializeComponents(): void {
    // Initialize header component
    new HeaderComponent();

    // Initialize search form component
    const searchForm = document.getElementById('flight-search-form') as HTMLFormElement;
    if (searchForm) {
      this.searchComponent = new FlightSearchComponent(searchForm);
    }

    // Initialize results component
    const resultsContainer = document.getElementById('flight-results');
    if (resultsContainer) {
      this.resultsComponent = new FlightResultsComponent(resultsContainer);
    }

    // Initialize filters component
    const filtersContainer = document.querySelector('.search-filters');
    if (filtersContainer) {
      this.filtersComponent = new FlightFiltersComponent(filtersContainer as HTMLElement);
    }
  }

  private bindEvents(): void {
    // Search form submission
    if (this.searchComponent) {
      this.searchComponent.onSearch = (searchData: FlightSearchData) => {
        this.handleSearch(searchData);
      };
    }

    // Filter changes
    if (this.filtersComponent) {
      this.filtersComponent.onFilterChange = (filters: FlightFilters) => {
        this.currentFilters = filters;
        this.applyFilters();
      };

      this.filtersComponent.onClearFilters = () => {
        this.currentFilters = {};
        this.applyFilters();
      };
    }

    // Results interactions
    if (this.resultsComponent) {
      this.resultsComponent.onFlightSelect = (flightId: string) => {
        this.handleFlightSelection(flightId);
      };

      this.resultsComponent.onSortChange = (sortBy: string) => {
        this.handleSortChange(sortBy);
      };

      this.resultsComponent.onPageChange = (page: number) => {
        this.handlePageChange(page);
      };
    }

    // Retry search button
    const retryButton = document.querySelector('.no-results__retry');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        this.showSearchForm();
      });
    }
  }

  private handleInitialSearch(): void {
    // Check if there are URL parameters for initial search
    const urlParams = new URLSearchParams(window.location.search);
    const origin = urlParams.get('origin');
    const destination = urlParams.get('destination');
    const departureDate = urlParams.get('departureDate');
    const passengers = urlParams.get('passengers');

    if (origin && destination && departureDate) {
      const returnDateParam = urlParams.get('returnDate');
      const searchData: FlightSearchData = {
        origin,
        destination,
        departureDate: new Date(departureDate),
        returnDate: returnDateParam ? new Date(returnDateParam) : undefined,
        passengers: passengers ? parseInt(passengers) : 1,
        class: (urlParams.get('class') as 'economy' | 'business' | 'first') || 'economy'
      };

      // Populate form with initial data
      if (this.searchComponent) {
        this.searchComponent.populateForm(searchData);
      }

      // Perform initial search
      this.handleSearch(searchData);
    }
  }

  private async handleSearch(searchData: FlightSearchData): Promise<void> {
    this.currentSearchData = searchData;
    this.showLoadingState();

    try {
      // Simulate API call - in real implementation, this would call actual flight search API
      const results = await this.searchFlights(searchData);
      
      if (results.length > 0) {
        this.showResults(results);
        this.updateFiltersWithResults(results);
      } else {
        this.showNoResults();
      }
    } catch (error) {
      console.error('Flight search error:', error);
      this.showError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    }
  }

  private async searchFlights(searchData: FlightSearchData): Promise<FlightResult[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock flight data - in real implementation, this would come from API
    return this.generateMockFlights(searchData);
  }

  private generateMockFlights(searchData: FlightSearchData): FlightResult[] {
    const airlines = [
      { name: 'Air France', code: 'AF', logo: '../assets/images/airlines/air-france-logo.png' },
      { name: 'Lufthansa', code: 'LH', logo: '../assets/images/airlines/lufthansa-logo.png' },
      { name: 'British Airways', code: 'BA', logo: '../assets/images/airlines/british-airways-logo.png' },
      { name: 'KLM', code: 'KL', logo: '../assets/images/airlines/klm-logo.png' },
      { name: 'Ryanair', code: 'FR', logo: '../assets/images/airlines/ryanair-logo.png' },
      { name: 'EasyJet', code: 'U2', logo: '../assets/images/airlines/easyjet-logo.png' }
    ];

    const flights: FlightResult[] = [];
    const basePrice = 150;

    for (let i = 0; i < 15; i++) {
      const airlineIndex = Math.floor(Math.random() * airlines.length);
      const selectedAirline = airlines[airlineIndex];
      if (!selectedAirline) continue;
      
      const stops = Math.floor(Math.random() * 3);
      const departureHour = 6 + Math.floor(Math.random() * 18);
      const duration = 2 + Math.floor(Math.random() * 8);
      
      const departureTime = new Date(searchData.departureDate);
      departureTime.setHours(departureHour, Math.floor(Math.random() * 60));
      
      const arrivalTime = new Date(departureTime);
      arrivalTime.setHours(arrivalTime.getHours() + duration);

      const price = basePrice + (Math.random() * 500) + (stops * 50);

      flights.push({
        id: `flight-${i + 1}`,
        airline: selectedAirline.name,
        airlineLogo: selectedAirline.logo,
        flightNumber: `${selectedAirline.code}${1000 + i}`,
        origin: searchData.origin,
        originCode: this.getAirportCode(searchData.origin),
        destination: searchData.destination,
        destinationCode: this.getAirportCode(searchData.destination),
        departureTime,
        arrivalTime,
        duration: `${duration}h ${Math.floor(Math.random() * 60)}min`,
        price: {
          min: Math.floor(price),
          max: Math.floor(price * 1.2),
          currency: 'EUR'
        },
        stops,
        stopDetails: stops > 0 ? this.generateStopDetails(stops) : undefined,
        aircraft: this.getRandomAircraft(),
        class: searchData.class || 'economy',
        baggage: {
          carry: '8kg',
          checked: stops === 0 ? 'Non inclus' : '23kg'
        },
        amenities: this.getRandomAmenities()
      });
    }

    return flights.sort((a, b) => a.price.min - b.price.min);
  }

  private getAirportCode(city: string): string {
    const codes: { [key: string]: string } = {
      'Paris': 'CDG',
      'London': 'LHR',
      'New York': 'JFK',
      'Tokyo': 'NRT',
      'Madrid': 'MAD',
      'Rome': 'FCO',
      'Berlin': 'BER',
      'Amsterdam': 'AMS'
    };
    return codes[city] || city.substring(0, Math.min(3, city.length)).toUpperCase();
  }

  private generateStopDetails(stops: number): string[] {
    const cities = ['Frankfurt', 'Amsterdam', 'London', 'Madrid', 'Zurich', 'Vienna'];
    return cities.slice(0, stops);
  }

  private getRandomAircraft(): string {
    const aircraft = ['Boeing 737', 'Airbus A320', 'Boeing 777', 'Airbus A350', 'Boeing 787', 'Airbus A380'];
    const selectedAircraft = aircraft[Math.floor(Math.random() * aircraft.length)];
    return selectedAircraft || 'Boeing 737';
  }

  private getRandomAmenities(): string[] {
    const allAmenities = ['WiFi', 'Repas inclus', 'Divertissement', 'Prise électrique', 'USB', 'Siège inclinable'];
    const count = Math.floor(Math.random() * 4) + 1;
    return allAmenities.slice(0, count);
  }

  private showLoadingState(): void {
    const searchResults = document.getElementById('search-results');
    const loadingState = document.getElementById('loading-state');
    const flightResults = document.getElementById('flight-results');
    const noResults = document.getElementById('no-results');

    if (searchResults) searchResults.style.display = 'block';
    if (loadingState) loadingState.style.display = 'block';
    if (flightResults) flightResults.style.display = 'none';
    if (noResults) noResults.style.display = 'none';
  }

  private showResults(flights: FlightResult[]): void {
    const loadingState = document.getElementById('loading-state');
    const flightResults = document.getElementById('flight-results');
    const noResults = document.getElementById('no-results');
    const resultsSummary = document.getElementById('results-summary');

    if (loadingState) loadingState.style.display = 'none';
    if (flightResults) flightResults.style.display = 'block';
    if (noResults) noResults.style.display = 'none';

    if (resultsSummary) {
      resultsSummary.textContent = `${flights.length} vol${flights.length > 1 ? 's' : ''} trouvé${flights.length > 1 ? 's' : ''}`;
    }

    if (this.resultsComponent) {
      this.resultsComponent.displayFlights(flights);
    }
  }

  private showNoResults(): void {
    const loadingState = document.getElementById('loading-state');
    const flightResults = document.getElementById('flight-results');
    const noResults = document.getElementById('no-results');

    if (loadingState) loadingState.style.display = 'none';
    if (flightResults) flightResults.style.display = 'none';
    if (noResults) noResults.style.display = 'block';
  }

  private showError(message: string): void {
    const loadingState = document.getElementById('loading-state');
    const flightResults = document.getElementById('flight-results');
    const noResults = document.getElementById('no-results');

    if (loadingState) loadingState.style.display = 'none';
    if (flightResults) flightResults.style.display = 'none';
    if (noResults) {
      noResults.style.display = 'block';
      const errorMessage = noResults.querySelector('.no-results__message');
      if (errorMessage) {
        errorMessage.textContent = message;
      }
    }
  }

  private showSearchForm(): void {
    const searchResults = document.getElementById('search-results');
    if (searchResults) {
      searchResults.style.display = 'none';
    }
    
    // Scroll to search form
    const searchSection = document.querySelector('.search-section');
    if (searchSection) {
      searchSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private updateFiltersWithResults(flights: FlightResult[]): void {
    if (this.filtersComponent) {
      this.filtersComponent.updateAvailableFilters(flights);
    }
  }

  private applyFilters(): void {
    if (this.resultsComponent && this.currentSearchData) {
      this.resultsComponent.applyFilters(this.currentFilters);
    }
  }

  private handleFlightSelection(flightId: string): void {
    // In a real application, this would navigate to booking page
    console.log('Flight selected:', flightId);
    alert(`Vol sélectionné: ${flightId}. Redirection vers la page de réservation...`);
  }

  private handleSortChange(sortBy: string): void {
    if (this.resultsComponent) {
      this.resultsComponent.sortResults(sortBy);
    }
  }

  private handlePageChange(page: number): void {
    if (this.resultsComponent) {
      this.resultsComponent.goToPage(page);
    }
  }
}

// Initialize the flights page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FlightsPage();
});